// frontend/src/pages/lobby/LobbyMySchedulePage.tsx
import { useEffect, type ReactNode } from 'react'
import { Badge, Box, Flex, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react'
import { FiMapPin, FiSettings } from 'react-icons/fi'
import { Link, useParams } from 'react-router-dom'
import { useAttendeeSchedule } from '../../hooks/useLobbyDay'
// Re-renders every 30s so the countdown stays honest and the hero flips on its own.
import { useNow } from '../../hooks/useNow'
import { StatusChip } from '../../components/shared/StatusChip'
import { AttendeeDeadEnd } from '../../components/lobby/AttendeeDeadEnd'
import { rememberAttendeeReturn } from '../../lib/attendeeReturnCredential'
import type { AttendeeMeetingDto } from '../../types/lobbyDay'
import {
  selectFocusMeeting, partitionRest, formatCountdown, startsToday, meetingStart, meetingEnd,
} from './upNext'

function fmtTime(t: string): string {
  const [h, m] = t.split(':').map(Number)
  const h12 = ((h + 11) % 12) + 1
  return `${h12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`
}

export function LobbyMySchedulePage() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, error } = useAttendeeSchedule(token)
  const now = useNow()

  // [LW-24 Slice 5, Tier 2] Remember this working link on this device so the attendee dead-end
  // can offer a "Welcome back" deep-link if they later arrive on a stale/expired link. Device-only.
  useEffect(() => {
    if (token && data) rememberAttendeeReturn({ token, eventName: data.eventName })
  }, [token, data])

  if (isLoading) return <Flex minH="100vh" align="center" justify="center"><Spinner size="lg" color="accent" /></Flex>
  if (!!error || !data) return <AttendeeDeadEnd currentToken={token} />

  const focus = selectFocusMeeting(data.meetings, now)
  const { upcoming, past } = partitionRest(data.meetings, now, focus.meeting?.meetingId ?? null)

  return (
    <Box minH="100vh" bg="canvas">
      <Box bg="band" color="white" px={4} py={3}>
        <Flex justify="space-between" align="center" gap={2}>
          <Box minW={0}>
            <Heading size="sm">{data.eventName}</Heading>
            <Text fontSize="xs" opacity={0.8}>
              {data.attendeeName} · {data.startDate}{data.eventLocation ? ` · ${data.eventLocation}` : ''}
            </Text>
          </Box>
          {/* Plain Box (not IconButton) so the icon inherits the band's mode-invariant
              light foreground via the "band.fg" token instead of the button recipe's
              own colorPalette.fg override -- see theme.ts's band.fg for the rationale. */}
          <Box
            asChild
            aria-label="Settings"
            color="band.fg"
            minW="44px"
            minH="44px"
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="md"
            _active={{ opacity: 0.7 }}
          >
            <Link to={`/lobby/${token}/settings`}><FiSettings /></Link>
          </Box>
        </Flex>
      </Box>

      <VStack align="stretch" gap={3} p={4} maxW="480px" mx="auto">
        {data.meetings.length === 0 ? (
          <Text color="fg.muted" textAlign="center" py={12}>No meetings scheduled yet.</Text>
        ) : (
          <>
            {focus.meeting
              ? <FocusHero
                  m={focus.meeting}
                  kind={focus.kind === 'now' ? 'now' : 'next'}
                  now={now}
                  eventLocation={data.eventLocation}
                  token={token!}
                />
              : <WrapUpCard />}

            {upcoming.length > 0 && (
              <>
                <GroupLabel>Later today</GroupLabel>
                {upcoming.map((m) => <MeetingRow key={m.meetingId} m={m} token={token!} />)}
              </>
            )}

            {past.length > 0 && (
              <>
                <GroupLabel>Earlier</GroupLabel>
                {past.map((m) => <MeetingRow key={m.meetingId} m={m} token={token!} past />)}
              </>
            )}
          </>
        )}
      </VStack>
    </Box>
  )
}

// One line under the legislator name: a drop-off outranks the staff note.
function MeetingNote({ m }: { m: AttendeeMeetingDto }) {
  if (m.status === 'DropOff') {
    return <Box mt={1}><StatusChip variant="warning">Drop off materials — the meeting was cancelled</StatusChip></Box>
  }
  if (m.withWhom === 'Staff' || m.withWhom === 'Both') {
    return <Text fontSize="sm" color="fg.muted" mt={1}>Meeting with staff</Text>
  }
  return null
}

function GroupLabel({ children }: { children: ReactNode }) {
  return (
    <Text fontSize="11px" fontWeight="bold" letterSpacing="wide" textTransform="uppercase" color="fg.muted" mt={1} px={1}>
      {children}
    </Text>
  )
}

function FocusHero({
  m, kind, now, eventLocation, token,
}: { m: AttendeeMeetingDto; kind: 'now' | 'next'; now: Date; eventLocation: string | null; token: string }) {
  const today = startsToday(m, now)
  const kicker = kind === 'now'
    ? 'Happening now'
    : today
      ? `Up next · in ${formatCountdown(meetingStart(m).getTime() - now.getTime())}`
      : 'Up next'
  const timeNote = kind === 'now'
    ? `ends in ${formatCountdown(meetingEnd(m).getTime() - now.getTime())}`
    : today
      ? `starts in ${formatCountdown(meetingStart(m).getTime() - now.getTime())}`
      : meetingStart(m).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
  const cta = kind === 'now' ? 'Open packet · record outcome →' : 'View packet →'

  return (
    <Box
      data-testid="day-of-hero"
      bg="accent.soft"
      borderWidth="1.5px"
      borderColor="accent"
      borderRadius="lg"
      p={4}
      _active={{ opacity: 0.9 }}
      asChild><Link to={`/lobby/${token}/meetings/${m.meetingId}`}>
        <Text fontSize="11px" fontWeight="bold" letterSpacing="wider" textTransform="uppercase" color="accent">
          {kicker}
        </Text>
        <Heading size="md" color="ink" mt={2}>{m.legislatorName}</Heading>
        <MeetingNote m={m} />
        <HStack gap={2} fontSize="sm" color="fg.muted" mt={1}>
          <Badge>{m.districtLabel}</Badge>
          {m.legislatorParty && <Text>{m.legislatorParty}</Text>}
        </HStack>

        <Flex justify="space-between" align="flex-end" mt={3} gap={3}>
          <Box>
            <Text fontSize="10px" fontWeight="bold" letterSpacing="wide" textTransform="uppercase" color="fg.muted">
              Room
            </Text>
            <Text fontSize="2xl" fontWeight="bold" color="ink" lineHeight="1">
              {m.room ?? 'TBD'}
            </Text>
          </Box>
          <Box textAlign="right">
            <Text fontSize="lg" fontWeight="bold" color="ink">{fmtTime(m.startTime)}</Text>
            <Text fontSize="xs" fontWeight="semibold" color="accent">{timeNote}</Text>
          </Box>
        </Flex>

        {eventLocation && (
          <HStack gap={1} mt={3} pt={3} borderTopWidth="1px" borderColor="border" color="ink2" fontSize="sm">
            <FiMapPin aria-hidden />
            <Text>{eventLocation}</Text>
          </HStack>
        )}
        <Text fontSize="sm" color="ink" mt={2}>{m.askTitle ?? 'Meeting'}</Text>
        {m.coAttendees.length > 0 && (
          <Text fontSize="xs" color="fg.muted" mt={1}>with {m.coAttendees.join(', ')}</Text>
        )}
        <Box mt={3} bg="accent" color="accent.fg" borderRadius="md" textAlign="center" py="11px" minH="44px" fontWeight="semibold" fontSize="sm">
          {cta}
        </Box>
      </Link></Box>
  )
}

function WrapUpCard() {
  return (
    <Box
      data-testid="day-of-hero"
      bg="surface"
      borderWidth="1px"
      borderColor="border"
      borderRadius="lg"
      p={6}
      textAlign="center"
    >
      <Heading size="sm" color="ink">All meetings finished</Heading>
      <Text fontSize="sm" color="fg.muted" mt={2}>
        Nothing left on your schedule today. Thanks for lobbying!
      </Text>
    </Box>
  )
}

function MeetingRow({ m, token, past }: { m: AttendeeMeetingDto; token: string; past?: boolean }) {
  return (
    <Box
      bg="surface"
      borderWidth="1px"
      borderColor="border"
      borderRadius="lg"
      p={3}
      opacity={past ? 0.55 : 1}
      _active={{ opacity: past ? 0.5 : 0.85 }}
      asChild><Link to={`/lobby/${token}/meetings/${m.meetingId}`}>
        <HStack justify="space-between" align="baseline">
          <Text fontWeight={past ? 'medium' : 'semibold'} color="ink">{m.legislatorName}</Text>
          <Text fontSize="sm" color="fg.muted">{fmtTime(m.startTime)}</Text>
        </HStack>
        <MeetingNote m={m} />
        <HStack gap={2} fontSize="sm" color="fg.muted">
          <Badge>{m.districtLabel}</Badge>
          {m.legislatorParty && <Text>{m.legislatorParty}</Text>}
          {m.room && <Text>· Rm {m.room}</Text>}
        </HStack>
        {!past && (
          <Text fontSize="sm" color="fg.muted" mt={1}>
            {m.askTitle ?? 'Meeting'}
            {m.coAttendees.length > 0 && ` · with ${m.coAttendees.join(', ')}`}
          </Text>
        )}
      </Link></Box>
  )
}
